import * as fs from 'fs-extra';
import * as path from 'path';
import { ProjectAnalyzer as IProjectAnalyzer, ProjectStructure, ProjectFile, ValidationResult, ProjectMetadata } from './types';

export class ProjectAnalyzer implements IProjectAnalyzer {
  async analyzeStructure(projectPath: string): Promise<ProjectStructure> {
    const packageJsonPath = path.join(projectPath, 'package.json');
    let packageJson = {};
    
    if (await fs.pathExists(packageJsonPath)) {
      packageJson = await fs.readJson(packageJsonPath);
    }

    // Analyze dezzy config
    let dezzyConfig;
    const configPath = path.join(projectPath, '.dezzy.json');
    if (await fs.pathExists(configPath)) {
      dezzyConfig = await fs.readJson(configPath);
    }

    // Collect all files
    const files = await this.collectFiles(projectPath);
    
    // Detect directories
    const directories = await this.getDirectories(projectPath);
    
    // Detect version
    const version = await this.detectVersion(projectPath, packageJson);

    return {
      path: projectPath,
      version,
      packageJson,
      dezzyConfig,
      files,
      directories
    };
  }

  async findDezzyProjects(rootPath: string): Promise<string[]> {
    const projects: string[] = [];
    
    if (!(await fs.pathExists(rootPath))) {
      return projects;
    }

    try {
      await this.searchForProjects(rootPath, projects, 0, 3); // Max depth 3
    } catch (error) {
      console.warn(`Error searching for projects in ${rootPath}:`, error);
    }

    return projects;
  }

  async validateDezzyProject(projectPath: string): Promise<ValidationResult> {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      // Check basic project structure
      if (!(await fs.pathExists(projectPath))) {
        errors.push('Project path does not exist');
        return { valid: false, errors };
      }

      // Check for package.json
      const packageJsonPath = path.join(projectPath, 'package.json');
      if (!(await fs.pathExists(packageJsonPath))) {
        errors.push('No package.json found');
      } else {
        const packageJson = await fs.readJson(packageJsonPath);
        
        // Check for dezzy dependency
        const hasDezzy = packageJson.dependencies?.dezzy || 
                         packageJson.devDependencies?.dezzy ||
                         packageJson.peerDependencies?.dezzy;
        
        if (!hasDezzy) {
          warnings.push('No dezzy dependency found - may not be a Dezzy project');
        }

        // Check for React (required for Dezzy)
        const hasReact = packageJson.dependencies?.react ||
                         packageJson.peerDependencies?.react;
        
        if (!hasReact) {
          errors.push('React dependency is required for Dezzy projects');
        }
      }

      // Check for src directory
      const srcPath = path.join(projectPath, 'src');
      if (!(await fs.pathExists(srcPath))) {
        warnings.push('No src directory found');
      } else {
        // Check for typical Dezzy structure
        const expectedDirs = ['components', 'tokens', 'stories'];
        const existingDirs = await fs.readdir(srcPath, { withFileTypes: true })
          .then(entries => entries.filter(e => e.isDirectory()).map(e => e.name));

        for (const dir of expectedDirs) {
          if (!existingDirs.includes(dir)) {
            suggestions.push(`Consider adding ${dir} directory`);
          }
        }
      }

      // Check for TypeScript support
      const tsconfigPath = path.join(projectPath, 'tsconfig.json');
      if (!(await fs.pathExists(tsconfigPath))) {
        suggestions.push('Consider adding TypeScript support');
      }

      // Check for Storybook
      const storybookPath = path.join(projectPath, '.storybook');
      if (!(await fs.pathExists(storybookPath))) {
        suggestions.push('Consider adding Storybook for component documentation');
      }

      return {
        valid: errors.length === 0,
        errors: errors.length > 0 ? errors : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
        suggestions: suggestions.length > 0 ? suggestions : undefined
      };
    } catch (error) {
      errors.push(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { valid: false, errors };
    }
  }

  async extractMetadata(projectPath: string): Promise<ProjectMetadata> {
    const packageJsonPath = path.join(projectPath, 'package.json');
    const packageJson = await fs.readJson(packageJsonPath).catch(() => ({}));
    
    const name = packageJson.name || path.basename(projectPath);
    const version = packageJson.version || '1.0.0';
    const dezzyVersion = await this.detectVersion(projectPath, packageJson);
    
    // Detect template
    const template = await this.detectTemplate(projectPath, packageJson);
    
    // Detect features
    const features = await this.detectFeatures(projectPath, packageJson);
    
    // Collect dependencies
    const dependencies = {
      ...packageJson.dependencies,
      ...packageJson.devDependencies
    };
    
    // Check migration history
    let lastMigration;
    const migrationHistoryPath = path.join(projectPath, '.dezzy', 'migrations.json');
    if (await fs.pathExists(migrationHistoryPath)) {
      try {
        const history = await fs.readJson(migrationHistoryPath);
        const migrations = history.migrations || [];
        if (migrations.length > 0) {
          const last = migrations[migrations.length - 1];
          lastMigration = {
            id: last.id,
            version: last.version,
            timestamp: new Date(last.timestamp)
          };
        }
      } catch (error) {
        console.warn('Failed to read migration history:', error);
      }
    }

    return {
      name,
      version,
      dezzyVersion,
      template,
      features,
      dependencies,
      lastMigration
    };
  }

  private async collectFiles(projectPath: string): Promise<ProjectFile[]> {
    const files: ProjectFile[] = [];
    const excludeDirs = [
      'node_modules',
      '.git',
      'dist',
      'build',
      '.next',
      'coverage',
      '.nyc_output',
      '.dezzy/backups'
    ];

    await this.walkDirectory(projectPath, '', files, excludeDirs);
    return files;
  }

  private async walkDirectory(
    basePath: string,
    relativePath: string,
    files: ProjectFile[],
    excludeDirs: string[]
  ): Promise<void> {
    const fullPath = path.join(basePath, relativePath);
    
    if (!(await fs.pathExists(fullPath))) {
      return;
    }

    const stats = await fs.stat(fullPath);
    
    if (stats.isFile()) {
      try {
        const content = await fs.readFile(fullPath, 'utf8');
        const type = this.detectFileType(relativePath);
        files.push({ path: relativePath, content, type });
      } catch (error) {
        // Skip files that can't be read as text
      }
    } else if (stats.isDirectory()) {
      const dirName = path.basename(relativePath);
      
      if (excludeDirs.includes(dirName)) {
        return;
      }

      try {
        const entries = await fs.readdir(fullPath);
        
        for (const entry of entries) {
          const entryPath = relativePath ? path.join(relativePath, entry) : entry;
          await this.walkDirectory(basePath, entryPath, files, excludeDirs);
        }
      } catch (error) {
        // Skip directories we can't read
      }
    }
  }

  private detectFileType(filePath: string): ProjectFile['type'] {
    const ext = path.extname(filePath).toLowerCase();
    
    switch (ext) {
      case '.ts':
      case '.tsx':
        return 'typescript';
      case '.js':
      case '.jsx':
        return 'javascript';
      case '.json':
        return 'json';
      case '.md':
        return 'markdown';
      case '.css':
      case '.scss':
      case '.sass':
      case '.less':
        return 'css';
      default:
        return 'other';
    }
  }

  private async getDirectories(projectPath: string): Promise<string[]> {
    const directories: string[] = [];
    
    try {
      await this.collectDirectories(projectPath, '', directories);
    } catch (error) {
      // Return empty array on error
    }
    
    return directories;
  }

  private async collectDirectories(
    basePath: string,
    relativePath: string,
    directories: string[]
  ): Promise<void> {
    const fullPath = path.join(basePath, relativePath);
    
    if (!(await fs.pathExists(fullPath))) {
      return;
    }

    const stats = await fs.stat(fullPath);
    if (!stats.isDirectory()) {
      return;
    }

    if (relativePath) {
      directories.push(relativePath);
    }

    try {
      const entries = await fs.readdir(fullPath, { withFileTypes: true });
      
      for (const entry of entries) {
        if (entry.isDirectory() && !entry.name.startsWith('.') && entry.name !== 'node_modules') {
          const entryPath = relativePath ? path.join(relativePath, entry.name) : entry.name;
          await this.collectDirectories(basePath, entryPath, directories);
        }
      }
    } catch (error) {
      // Skip directories we can't read
    }
  }

  private async searchForProjects(
    currentPath: string,
    projects: string[],
    depth: number,
    maxDepth: number
  ): Promise<void> {
    if (depth > maxDepth) {
      return;
    }

    // Check if current path is a Dezzy project
    const validation = await this.validateDezzyProject(currentPath);
    if (validation.valid || (validation.warnings && validation.warnings.length > 0)) {
      projects.push(currentPath);
      return; // Don't search subdirectories of found projects
    }

    // Search subdirectories
    try {
      const entries = await fs.readdir(currentPath, { withFileTypes: true });
      
      for (const entry of entries) {
        if (entry.isDirectory() && 
            !entry.name.startsWith('.') && 
            entry.name !== 'node_modules') {
          const subPath = path.join(currentPath, entry.name);
          await this.searchForProjects(subPath, projects, depth + 1, maxDepth);
        }
      }
    } catch (error) {
      // Skip directories we can't read
    }
  }

  private async detectVersion(projectPath: string, packageJson: any): Promise<string> {
    // Try to detect from dezzy dependency
    const dezzyVersion = packageJson.dependencies?.dezzy ||
                        packageJson.devDependencies?.dezzy ||
                        packageJson.peerDependencies?.dezzy;
    
    if (dezzyVersion) {
      return dezzyVersion.replace(/^[\^~]/, '').split('-')[0];
    }

    // Try to detect from project structure
    if (await fs.pathExists(path.join(projectPath, 'AGENT.md'))) {
      return '2.0.0';
    }

    if (await fs.pathExists(path.join(projectPath, 'src', 'agent.ts'))) {
      return '2.0.0';
    }

    // Check for enhanced manifests (v1.1.0+)
    const srcPath = path.join(projectPath, 'src');
    if (await fs.pathExists(srcPath)) {
      const files = await fs.readdir(srcPath, { withFileTypes: true });
      
      for (const file of files) {
        if (file.isFile() && file.name.endsWith('.manifest.json')) {
          try {
            const manifestPath = path.join(srcPath, file.name);
            const manifest = await fs.readJson(manifestPath);
            
            if (manifest.category || manifest.accessibility) {
              return '1.1.0';
            }
          } catch (error) {
            // Skip invalid JSON files
          }
        }
      }
    }

    // Default to 1.0.0
    return '1.0.0';
  }

  private async detectTemplate(projectPath: string, packageJson: any): Promise<string | undefined> {
    const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };
    
    if (deps.next) return 'nextjs';
    if (deps.vite) return 'vite-react';
    if (deps['react-scripts']) return 'react-app';
    
    // Check for Storybook-only setup
    if (deps['@storybook/react'] && !deps.next && !deps.vite && !deps['react-scripts']) {
      return 'storybook-only';
    }

    return undefined;
  }

  private async detectFeatures(projectPath: string, packageJson: any): Promise<string[]> {
    const features: string[] = [];
    const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };

    // Testing
    if (deps.jest || deps.vitest || deps['@testing-library/react']) {
      features.push('testing');
    }

    // Linting
    if (deps.eslint || deps.prettier) {
      features.push('linting');
    }

    // Storybook
    if (deps['@storybook/react']) {
      features.push('storybook');
    }

    // TypeScript
    if (deps.typescript || await fs.pathExists(path.join(projectPath, 'tsconfig.json'))) {
      features.push('typescript');
    }

    // AI Agent
    if (await fs.pathExists(path.join(projectPath, 'AGENT.md')) ||
        await fs.pathExists(path.join(projectPath, 'src', 'agent.ts'))) {
      features.push('ai-agent');
    }

    // Dark mode (check for theme-related files)
    if (await this.hasDarkModeSupport(projectPath)) {
      features.push('dark-mode');
    }

    return features;
  }

  private async hasDarkModeSupport(projectPath: string): Promise<boolean> {
    // Check tokens directory for theme files
    const tokensPath = path.join(projectPath, 'src', 'tokens');
    if (await fs.pathExists(tokensPath)) {
      try {
        const files = await fs.readdir(tokensPath);
        return files.some(f => f.includes('dark') || f.includes('theme'));
      } catch {
        return false;
      }
    }

    // Check for CSS custom properties with theme support
    const files = await this.collectFiles(projectPath);
    const cssFiles = files.filter(f => f.type === 'css');
    
    for (const file of cssFiles) {
      if (file.content.includes('--theme-') || 
          file.content.includes('[data-theme="dark"]') ||
          file.content.includes('.dark')) {
        return true;
      }
    }

    return false;
  }

  // Utility methods for analysis
  async getProjectSize(projectPath: string): Promise<{
    files: number;
    lines: number;
    components: number;
    tests: number;
    stories: number;
  }> {
    const structure = await this.analyzeStructure(projectPath);
    
    let lines = 0;
    let components = 0;
    let tests = 0;
    let stories = 0;
    
    for (const file of structure.files) {
      lines += file.content.split('\n').length;
      
      if (file.path.includes('components') && 
          (file.type === 'typescript' || file.type === 'javascript')) {
        components++;
      }
      
      if (file.path.includes('test') || file.path.includes('spec')) {
        tests++;
      }
      
      if (file.path.includes('stories')) {
        stories++;
      }
    }
    
    return {
      files: structure.files.length,
      lines,
      components,
      tests,
      stories
    };
  }

  async getDependencyAnalysis(projectPath: string): Promise<{
    total: number;
    outdated: number;
    vulnerabilities: number;
    bundleSize: number;
  }> {
    try {
      const structure = await this.analyzeStructure(projectPath);
      const deps = structure.packageJson.dependencies || {};
      const devDeps = structure.packageJson.devDependencies || {};
      
      const total = Object.keys(deps).length + Object.keys(devDeps).length;
      
      // These would require actual package analysis in a real implementation
      return {
        total,
        outdated: 0, // Would require npm outdated analysis
        vulnerabilities: 0, // Would require npm audit
        bundleSize: 0 // Would require bundle analyzer
      };
    } catch (error) {
      return { total: 0, outdated: 0, vulnerabilities: 0, bundleSize: 0 };
    }
  }
}